/**
 * Utilidades para peticiones API
 * Proporciona funciones helper para configurar headers de autenticación
 */

import { environment } from '../config/environment';

/** Encode JSON for HTTP headers (fetch requires ISO-8859-1 header values). */
function encodeUserInfoForHeader(userInfo: unknown): string {
  const json = JSON.stringify(userInfo);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Función helper para obtener datos del usuario desde localStorage (misma lógica que el botón "Datos de Usuario")
function getUserInfoFromStorage() {
  try {
    const userInfoStr = localStorage.getItem('petflix_user_info');
    if (userInfoStr) {
      return JSON.parse(userInfoStr);
    }
    return null;
  } catch (e) {
    console.warn('Error obteniendo información del usuario desde localStorage:', e);
    return null;
  }
}

/**
 * Obtiene los headers básicos para peticiones API incluyendo autenticación
 * @param additionalHeaders - Headers adicionales a incluir
 * @returns Objeto con headers configurados incluyendo x-api-key
 */
export function getApiHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  // Agregar x-api-key si está disponible
  const accessKey = environment.petContestAccessKey;
  if (accessKey && accessKey.trim() !== '') {
    headers['x-api-key'] = accessKey;
  } else {
    console.warn('⚠️ REACT_APP_PET_CONTEST_ACCESS_KEY no está configurada. Las peticiones pueden fallar con 401.');
  }

  // Agregar datos del usuario desde localStorage (misma lógica que el botón "Datos de Usuario")
  const userInfo = getUserInfoFromStorage();
  if (userInfo) {
    try {
      headers['x-petflix-user-info'] = encodeUserInfoForHeader(userInfo);
    } catch (e) {
      console.warn('Error serializando información del usuario:', e);
    }
  }

  return headers;
}

/**
 * Obtiene los headers para peticiones GET (sin Content-Type)
 * @param additionalHeaders - Headers adicionales a incluir
 * @returns Objeto con headers configurados incluyendo x-api-key
 */
export function getApiHeadersForGet(additionalHeaders: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = {
    ...additionalHeaders
  };

  // Agregar x-api-key si está disponible
  const accessKey = environment.petContestAccessKey;
  if (accessKey && accessKey.trim() !== '') {
    headers['x-api-key'] = accessKey;
  } else {
    console.warn('⚠️ REACT_APP_PET_CONTEST_ACCESS_KEY no está configurada. Las peticiones pueden fallar con 401.');
  }

  // Agregar datos del usuario desde localStorage (misma lógica que el botón "Datos de Usuario")
  const userInfo = getUserInfoFromStorage();
  if (userInfo) {
    try {
      headers['x-petflix-user-info'] = encodeUserInfoForHeader(userInfo);
    } catch (e) {
      console.warn('Error serializando información del usuario:', e);
    }
  }

  return headers;
}

export type CouponPassQRLinkEntry = {
  link: string | null;
  is_claimed: boolean;
  tagType?: string;
  tag_code?: string | null;
};

export type CouponPassQRLinksResult = {
  linksMap: Record<string, string | null>;
  claimedMap: Record<string, boolean>;
  tagTypesMap: Record<string, string>;
  tagCodesMap: Record<string, string | null>;
};

const QR_LINKS_BULK_CHUNK = 150;

/**
 * Fetch QR link metadata for many CouponPasses via the bulk endpoint (avoids 429).
 */
export async function fetchCouponPassQRLinksBulk(ids: string[]): Promise<CouponPassQRLinksResult> {
  const linksMap: Record<string, string | null> = {};
  const claimedMap: Record<string, boolean> = {};
  const tagTypesMap: Record<string, string> = {};
  const tagCodesMap: Record<string, string | null> = {};

  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (uniqueIds.length === 0) {
    return { linksMap, claimedMap, tagTypesMap, tagCodesMap };
  }

  for (let i = 0; i < uniqueIds.length; i += QR_LINKS_BULK_CHUNK) {
    const chunk = uniqueIds.slice(i, i + QR_LINKS_BULK_CHUNK);
    const url = `${environment.apiBaseUrl}/couponpasses/qr-links?ids=${encodeURIComponent(chunk.join(','))}`;

    let data: Record<string, CouponPassQRLinkEntry> = {};
    for (let attempt = 1; attempt <= 3; attempt++) {
      const response = await fetch(url, { headers: getApiHeadersForGet() });
      if (response.ok) {
        data = await response.json();
        break;
      }
      if (response.status === 429 && attempt < 3) {
        await new Promise(r => setTimeout(r, attempt * 1200));
        continue;
      }
      break;
    }

    chunk.forEach((id) => {
      const entry = data[id];
      linksMap[id] = entry?.link ?? null;
      claimedMap[id] = entry?.is_claimed ?? false;
      tagTypesMap[id] = entry?.tagType ?? 'standard';
      tagCodesMap[id] = entry?.tag_code ?? null;
    });
  }

  return { linksMap, claimedMap, tagTypesMap, tagCodesMap };
}

